<template>
    <v-autocomplete 
        autocomplete="off"
        outlined
        dense
        hide-details
        :value="value"
        :items="items"
        :disabled="disabled"
        :readonly="readonly"
        :background-color="backgroundColor"
        :item-text="getItemText"
        return-object
        :loading="loading"
        :search-input.sync="search"
        :filter="filterBy"
        :no-data-text="'No se han encontrado coincidencias'"
        :error="error"
        @input="onInput"
        @change="onChange"
    >
        <template slot="item" slot-scope="data">
            <span v-if="data.item.id == -1">
                Nuevo
            </span>
            <span v-else>
                {{ getItemText(data.item) }}
            </span>
        </template>
    </v-autocomplete>
</template>

<script>
export default {
    name: 'PartyAutocomplete',
    props: {
        value: {
            type: Object,
            default: null,
        },
        invoiceableRepo: {
            type: Object,
            required: false,
            default: null
        },
        companyId: {
            type: [Number, String],
            required: false,
            default: null
        },
        partyOptions: {
            type: Array,
            default: () => [],
        },
        disabled: {
            type: Boolean,
            default: false
        },
        readonly: {
            type: Boolean,
            default: false
        },
        backgroundColor: {
            type: String,
            default: 'white'
        },
        error: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            items: [],
            search: null,
            loading: false,
            api_call: 0,
            searchTimeout: null
        }
    },
    methods: {
        onInput(value) {
            this.$emit('input', value);
        },
        onChange(value) {
            this.$emit('change', value);
        },
        getItemText(item) {
            if (!item) return '';
            if (item.id === -1) return 'Nuevo';
            if (item.type == 'fisica') {
                return `${item.first_surname || ''} ${item.second_surname || ''}, ${item.name || ''}`.trim();
            }
            return item.business_name || '';
        },
        getItems(val) {
            // Si no hay repo, usar partyOptions estático
            if (!this.invoiceableRepo) {
                this.items = this.partyOptions;
                // Asegurarse de que el valor actual esté en items si existe
                if (this.value && !this.items.some(item => item.id === this.value.id)) {
                    this.items = [this.value, ...this.items];
                }
                return;
            }

            this.loading = true;
            this.api_call += 1;
            let curr_call = this.api_call;

            this.invoiceableRepo.get_customer_list(this.companyId, val || '')
                .then(results => {
                    if (curr_call == this.api_call) {
                        this.items = results || [];
                        // Agregar opción "Nuevo" si no existe
                        if (!this.items.some(item => item.id == -1)) {
                            this.items.unshift({id: -1, name: 'Nuevo', type: 'fisica'});
                        }
                        // Asegurarse de que el valor actual esté en items
                        if (this.value && !this.items.some(item => item.id === this.value.id) && this.value.id !== -1) {
                            this.items.push(this.value);
                        }
                    }
                })
                .catch(error => { 
                    console.error('Error al obtener lista de clientes:', error);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        filterBy(item, queryText, itemText) {
            if (!queryText) return true;
            if (item.id == -1) return true; // Siempre mostrar "Nuevo"
            
            const searchLower = queryText.toLowerCase();
            
            try {
                if (item.type == 'fisica') {
                    const first = (item.first_surname || '').toLowerCase();
                    const second = (item.second_surname || '').toLowerCase();
                    const name = (item.name || '').toLowerCase();
                    const account = (item.debit_account || '').toLowerCase();
                    // also search in combinations of fields for multi-word searches
                    const fullName = `${first} ${second} ${name}`;
                    const nameFirst = `${name} ${first}`;
                    return first.includes(searchLower) ||
                           second.includes(searchLower) ||
                           name.includes(searchLower) ||
                           account.includes(searchLower) ||
                           fullName.includes(searchLower) ||
                           nameFirst.includes(searchLower);
                } else {
                    const business = (item.business_name || '').toLowerCase();
                    const account = (item.debit_account || '').toLowerCase();
                    return business.includes(searchLower) || account.includes(searchLower);
                }
            } catch (e) {
                return true;
            }
        },
    },
    mounted() {
        // Inicializar items y search
        if (this.value) {
            if (this.invoiceableRepo) {
                const searchText = this.getItemText(this.value);
                this.search = searchText;
                this.getItems(searchText);
            } else {
                // Si no hay repo, asegurarse de que el valor esté en items
                if (!this.items.some(item => item.id === this.value.id)) {
                    this.items = [this.value, ...this.partyOptions];
                }
                this.search = this.getItemText(this.value);
            }
        } else {
            this.getItems('');
        }
    },
    watch:{
        value: {
            handler(val) {
                // Cuando cambia el value desde fuera, asegurarse de que esté en items
                if (val && Object.keys(val).length > 0) {
                    // Verificar si el valor actual está en items
                    const existsInItems = this.items.some(item => item.id === val.id);
                    
                    if (!existsInItems && val.id !== -1) {
                        // Si no está en items y no es "Nuevo", agregarlo
                        this.items = [val, ...this.items];
                    }
                    
                    // Actualizar el search input para mostrar el texto
                    this.$nextTick(() => {
                        this.search = this.getItemText(val);
                    });
                    
                    if (this.invoiceableRepo) {
                        const searchText = this.getItemText(val);
                        this.getItems(searchText);
                    }
                } else if (!val) {
                    this.search = '';
                    if (this.invoiceableRepo) {
                        this.getItems('');
                    }
                }
            },
            immediate: false
        },
        search(val) {
            // Cancelar timeout anterior si existe
            if (this.searchTimeout) {
                clearTimeout(this.searchTimeout);
            }
            
            // Crear nuevo timeout para hacer la búsqueda después de 300ms de inactividad
            this.searchTimeout = setTimeout(() => {
                this.getItems(val);
            }, 300);
        },
        partyOptions: {
            handler(newVal) {
                // Si no hay repo, actualizar items cuando cambian las opciones
                if (!this.invoiceableRepo) {
                    this.items = newVal;
                }
            },
            deep: true
        }
    },
    beforeDestroy() {
        // Limpiar timeout al destruir el componente
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
        }
    }
}
</script>

<style scoped>
</style>
